import { TOUR_ANCHOR, TourAnchor } from './tourAnchors';
import { TOUR_STEP, TourStepKey } from './tourSteps';
import { DOCS } from './tourData';

// Guided walkthrough of the Add Copier drawer, one section at a time.
// Copy is taken from the product docs so the tour and the docs cannot drift:
//   docs.tradersconnect.com/trade-copier/risk-types
//   docs.tradersconnect.com/trade-copier/copier-options

export interface WalkthroughStop {
  anchor: TourAnchor;
  title: string;
  // HTML — driver.js popovers render markup, and the option lists need it.
  description: string;
  docsUrl: string;
  docsLabel: string;
  // Where the popover sits relative to the section. Defaults to the left, so it
  // never covers the very control it is describing inside a right-hand drawer.
  side?: 'left' | 'right' | 'top' | 'bottom';
}

const li = (name: string, body: string) => `<li><strong>${name}</strong>: ${body}</li>`;
const list = (intro: string, items: string[]) => `<p>${intro}</p><ul style="margin:8px 0 0;padding-left:18px">${items.join('')}</ul>`;

export const COPIER_WALKTHROUGH: WalkthroughStop[] = [
  {
    anchor: TOUR_ANCHOR.COPIER_RISK_SECTION,
    title: 'Risk Settings',
    docsUrl: DOCS.riskTypes,
    docsLabel: 'Risk types docs',
    description: list('Pick the master to copy from, the slave to copy to, and how position size is translated between them.', [
      li('Fixed Lot', 'every copied trade opens at the same lot size, whatever the master did.'),
      li(
        'Lot Multiplier',
        'slave lot = master lot × your %. At 100% a 10-lot trade copies as 10; at 50% as 5. Use this if you size by lots.'
      ),
      li(
        'Balance Multiplier',
        'multiplies the balance-% risk. A 1% master trade at 50% risks 0.5% on the slave. Use this if you size by % of balance.'
      ),
      li(
        'Fixed Balance Multiplier',
        'overrides the master balance with a fixed figure (e.g. $100,000), so the slave compounds on its own growth.'
      ),
      li(
        'Fixed % Risk (BETA)',
        'risks a set % of slave balance per trade. The master trade <em>must</em> carry a Stop Loss. Without one the lot size cannot be calculated.'
      )
    ])
  },
  {
    anchor: TOUR_ANCHOR.COPIER_SLTP_SECTION,
    title: 'SL & TP Settings',
    docsUrl: DOCS.copierOptions,
    docsLabel: 'Copier options docs',
    description: list('How stops and targets travel from master to slave.', [
      li(
        'Copy Stop Loss / Take Profit',
        'puts the master values on the slave as a safety net. With them off the slave still closes when the master closes.'
      ),
      li('Copy Pending Orders', 'on, pendings copy as pendings; off, they reach the slave as market orders once the master fills.'),
      li('Fixed Stop Loss / Take Profit', 'a uniform SL/TP in pips on every trade, whether or not the master set one.'),
      li('SL / TP Refinement', 'adds pips to the master value, so a 10-pip stop with 5 pips refinement becomes 15 on the slave.')
    ])
  },
  {
    anchor: TOUR_ANCHOR.COPIER_ADVANCED_SECTION,
    title: 'Advanced Settings',
    docsUrl: DOCS.copierOptions,
    docsLabel: 'Copier options docs',
    description: list('The behaviour switches. Two of these can catch you out, so read those first.', [
      li('Strict Close', '⚠️ the copier ignores every close from the master, so you <em>must</em> trade with SL and TP set.'),
      li('Reverse Trade', '⚠️ buys become sells. Cannot be used with Copy SL or Copy TP, because the values would be wrong.'),
      li('Force Min / Max Lot Size', "clamps a calculated size to the symbol's own minimum or maximum."),
      li(
        'Contract Alignment',
        'accounts for brokers using different contract sizes per lot. On by default for Balance Multiplier; switch it on yourself for the others.'
      ),
      li('Copy Existing Trades', 'copies the open master trades once, when the copier is first set up.'),
      li('Copy Expiry Time', 'carries pending-order expiry across. On by default.'),
      li('Copy Trade Comment', 'No, Yes, or Custom. With Custom you supply the comment written on slave trades.'),
      li('Lot Refiner', 'nudges the calculated lot by a fixed amount, positive or negative (e.g. 0.05 or -0.05).'),
      li('Trade Delay (ms)', 'delays entries by a fixed number of milliseconds, or a random value between two.')
    ])
  },
  {
    anchor: TOUR_ANCHOR.COPIER_FILTERS_SECTION,
    title: 'Trade Filters',
    docsUrl: DOCS.copierOptions,
    docsLabel: 'Copier options docs',
    description: list('Decide which master trades are eligible before any of the above applies.', [
      li('Filter by Magic Number', 'Copy only the listed numbers, or Ignore them. Comma-separated.'),
      li('Filter by Comment', 'same idea on the trade comment: Copy only these, or Ignore these.'),
      li('Filter by Trade Side', 'copy everything, buys only, or sells only.'),
      li('Filter by Lot Size', 'a min and max on the <em>master</em> lot size; trades outside the range are skipped.')
    ])
  }
];

// Billing — what the choices in the checkout drawer actually mean. Most of the
// confusion here is CFD vs Futures being separate purchases, so lead with that.
// The payment step, once Proceed has been pressed: the card, then the key.
export const PAYMENT_WALKTHROUGH: WalkthroughStop[] = [
  {
    anchor: TOUR_ANCHOR.CHECKOUT_CARD,
    title: 'Your card',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description: '<p>Pick a saved card or add a new one. Card details go straight to the payment processor — we never see them.</p>'
  },
  {
    anchor: TOUR_ANCHOR.CHECKOUT_PAY,
    title: 'Complete purchase',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description:
      '<p>The exact amount due today is shown above the key. Press it and your accounts unlock straight away — cancel any time from this page.</p>',
    side: 'top'
  }
];

export const BILLING_WALKTHROUGH: WalkthroughStop[] = [
  {
    anchor: TOUR_ANCHOR.CHECKOUT_CYCLE,
    title: 'Billing cycle',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description: list('How often you are charged. The longer the cycle, the lower the effective monthly price.', [
      li('Monthly', 'pay as you go, cancel any time.'),
      li('Quarterly', 'billed every 3 months.'),
      li('Semi-annual', 'billed every 6 months, and one month is free.'),
      li('Annual', 'billed once a year, and two months are free.')
    ])
  },
  {
    anchor: TOUR_ANCHOR.CHECKOUT_TIERS,
    title: 'CFD and Futures are separate',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description: list(
      'You buy licences per connected account, and the two families are priced and pooled separately, so a CFD licence cannot host a futures account, or the reverse.',
      [
        li('Standard (CFD)', '$10/mo per account. MT4, MT5, cTrader, DXtrade, TradeLocker, MatchTrader.'),
        li('Premium (CFD)', '$15/mo per account. Adds lower latency, HFT support and priority support.'),
        li(
          'Futures',
          'from $30/mo including 20 accounts, then $2 each, or a flat $120/mo unlimited. Tradovate, Rithmic, NinjaTrader, ProjectX.'
        ),
        li(
          'Why separate',
          'futures accounts trade contracts, not lots, so they route through different infrastructure and are counted in their own seat pool.'
        )
      ]
    )
  },
  {
    anchor: TOUR_ANCHOR.CHECKOUT_ADDONS,
    title: 'Add-ons',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description: list('Optional extras on top of your licences. None of these are needed to copy trades.', [
      li(
        'Dedicated Environment',
        'your own IP and hardware instead of the shared copier pool, at $10/mo per extra account. Traders on prop firms that flag shared IPs take this.'
      ),
      li('Analyzer', 'analytics on every copied and source order: ROI, win rate, drawdown, per-account equity curves.')
    ])
  },
  {
    anchor: TOUR_ANCHOR.CHECKOUT_TOTAL,
    title: 'Your total',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description: list('What you will actually be charged.', [
      li('Remember', 'a copier links two accounts, so pick at least 2 to follow the rest of this tour.'),
      li('Proration', 'items added mid-cycle are charged pro rata now, then in full from the next renewal.')
    ])
  },
  {
    anchor: TOUR_ANCHOR.CHECKOUT_PROCEED,
    title: 'Proceed to payment',
    docsUrl: DOCS.billing,
    docsLabel: 'Billing docs',
    description:
      '<p>Happy with the ticket? Proceed — the next step takes the card and shows the exact total before anything is charged.</p>',
    side: 'top'
  }
];

// Adding an account — one stop, since the form itself is broker-specific.
export const ACCOUNT_WALKTHROUGH: WalkthroughStop[] = [
  {
    anchor: TOUR_ANCHOR.ACCOUNT_PLATFORM_PICKER,
    title: 'Pick your platform',
    docsUrl: DOCS.accounts,
    docsLabel: 'Trading accounts docs',
    description: list('Start with the platform the account lives on. The fields below change to match it.', [
      li('CFD platforms', 'MT4, MT5, cTrader, DXtrade, TradeLocker and MatchTrader use a Standard or Premium licence.'),
      li(
        'Futures platforms',
        'Tradovate, Rithmic, NinjaTrader and ProjectX use a Futures licence and connect by sign-in rather than a password.'
      ),
      li('Master or slave', 'an account is not fixed as either; you decide direction later, when you build the copier.'),
      li('You need two', 'one to copy from and one to copy to. They cannot be the same account.')
    ])
  }
];

// Which walkthrough belongs to which tour step.
export const WALKTHROUGH_BY_STEP: Partial<Record<TourStepKey, WalkthroughStop[]>> = {
  [TOUR_STEP.plans]: BILLING_WALKTHROUGH,
  [TOUR_STEP.accounts]: ACCOUNT_WALKTHROUGH,
  [TOUR_STEP.copier]: COPIER_WALKTHROUGH
};
